import { useState } from 'react';
import { LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Principal } from '@dru/types';
import { Alert, Button, IconAction, TextField, cn } from '@dru/ui';
import { api } from '../../../lib/api';
import { useAuth, errorMessage } from '../../../lib/auth';
import { Dialog } from '../../../components/Dialog';
import { landingFor } from '../../../layouts/ConsoleShell';

/**
 * Where an impersonated session starts.
 *
 * Not a fixed HR route. This dialog is reached from two places that mean very
 * different things: a company's own page, where the target is an HR user and
 * the setup guide is usually the point, and the platform users screen, where
 * the target is one of the platform's own operators. Sending a platform
 * finance account to `/hr/onboarding` put them on a screen their role holds no
 * permission for, so the first thing the administrator saw of the session they
 * had just justified was "Missing required permission: settings.read".
 *
 * So the destination is computed from the permissions the impersonated
 * principal actually holds — the same `landingFor` that decides where anybody
 * lands at sign-in, which is the only thing that can be right for both cases.
 * Setting a company up is done from its Configuration tab, not by
 * impersonating its HR (docs/38), so there is no setup-guide exception.
 */
export function landingAfterImpersonation(
  principal: Principal | null | undefined,
  mode: 'full' | 'read_only',
): string {
  void mode;
  return landingFor(new Set(principal?.permissions ?? []), principal);
}

/**
 * Seeing the console as somebody else does.
 *
 * Shared by the company page, company setup and the platform users screens,
 * so a privileged act reads the same wherever it starts. Before anything is
 * minted the dialog says, in this order: who you will act as and where; what
 * you will be able to do (read only, or their full access); that it is
 * recorded against your name with the reason you give; and how it ends —
 * by itself after fifteen minutes, or from the banner on every screen.
 *
 * Read only is the default. Full access is chosen, not fallen into: it is
 * the setup path's starting point (`initialMode="full"`) and nobody else's.
 */
export function ImpersonateDialog({ user, where, initialMode = 'read_only', onClose }: {
  user: { id: string; fullName: string; roles: string[] };
  /** Whose console this is, for the sentence that explains it. */
  where?: string;
  initialMode?: 'full' | 'read_only';
  onClose: () => void;
}) {
  const { impersonate } = useAuth();
  const nav = useNavigate();
  // A company's own user is reached from that company's pages, which pass
  // `where`; the platform's own operators are reached from the users screen,
  // which does not. Onboarding is a thing you do to a company, so the wording
  // and the suggested reason follow that.
  const forCompanyUser = Boolean(where);
  const [mode, setMode] = useState<'full' | 'read_only'>(initialMode);
  // Always the operator's own words. A pre-filled "assisted onboarding"
  // reason put that sentence in the audit trail for sessions nobody explained.
  const [reason, setReason] = useState('');
  const [ticketRef, setTicketRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // The server's minimum, so the refusal is never a surprise.
  const tooShort = reason.trim().length < 10;

  async function start() {
    setTouched(true);
    if (tooShort) return;
    setBusy(true); setError(null);
    try {
      const res = await api.post<{ accessToken: string }>(`/admin/users/${user.id}/impersonate`, {
        reason: reason.trim(),
        mode,
        ...(ticketRef.trim() ? { ticketRef: ticketRef.trim() } : {}),
      });
      const principal = await impersonate(res.accessToken);
      nav(landingAfterImpersonation(principal, mode), { replace: true });
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  const roles = user.roles.join(', ') || 'no role';
  const MODES: Array<{ key: 'read_only' | 'full'; title: string; body: string }> = [
    {
      key: 'read_only',
      title: 'Read only',
      body: 'See their screens as they do. Anything that would save a change is refused.',
    },
    {
      key: 'full',
      title: 'Full access',
      body: forCompanyUser
        ? 'Act as them: add plants, users and settings to finish setup on their behalf.'
        : 'Act as them, with everything this operator\u2019s own roles allow.',
    },
  ];

  return (
    <Dialog title={`Sign in as ${user.fullName}`} onClose={onClose}>
      <div className="mt-2 rounded-card border border-neutral-200 bg-neutral-50 px-3 py-2.5">
        <p className="text-caption text-neutral-500">You will act as</p>
        <p className="text-body font-semibold text-neutral-900">{user.fullName}</p>
        <p className="text-small text-neutral-600">
          {roles}{where ? ` at ${where}` : ''}
        </p>
      </div>

      <fieldset className="mt-4">
        <legend className="text-caption font-medium text-neutral-700">Access</legend>
        <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
          {MODES.map((m) => (
            <label
              key={m.key}
              className={cn(
                'flex cursor-pointer items-start gap-2.5 rounded-card border p-3 transition-colors',
                mode === m.key
                  ? m.key === 'full'
                    ? 'border-warning-600 bg-warning-100 ring-1 ring-warning-600'
                    : 'border-primary-600 bg-primary-50 ring-1 ring-primary-600'
                  : 'border-neutral-200 hover:border-neutral-300',
              )}
            >
              <input
                type="radio"
                name="impersonation-mode"
                value={m.key}
                checked={mode === m.key}
                onChange={() => setMode(m.key)}
                className="mt-1 h-4 w-4 accent-primary-600"
              />
              <span>
                <span className="block text-small font-semibold text-neutral-900">{m.title}</span>
                <span className="mt-0.5 block text-caption font-normal text-neutral-600">{m.body}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {mode === 'full' && (
        <Alert tone="warning" className="mt-3">
          Changes you make are saved as {user.fullName}&rsquo;s, and the audit trail records that you made them.
        </Alert>
      )}

      <div className="mt-4 space-y-3">
        <TextField
          label="Reason"
          required
          value={reason}
          onChange={setReason}
          autoFocus
          error={touched && tooShort ? 'Write at least 10 characters — it is recorded in the audit trail.' : null}
          placeholder={forCompanyUser ? 'e.g. Helping HR import their first employees' : 'e.g. Reproducing ticket SUP-1234'}
        />
        <TextField label="Ticket reference" value={ticketRef} onChange={setTicketRef} mono placeholder="Optional, e.g. SUP-1234" />
      </div>

      <ul className="mt-4 space-y-1 text-caption font-normal text-neutral-600">
        <li>&bull; Recorded in the audit trail under your own name, with this reason.</li>
        <li>&bull; Ends by itself after 15 minutes. End it sooner with <strong className="font-semibold">End session</strong> in the banner at the top of every screen.</li>
      </ul>

      {error && <Alert tone="danger" className="mt-3">{error}</Alert>}
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={start} loading={busy}>
          {mode === 'full' ? 'Start full-access session' : 'Start read-only session'}
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * Why a platform user cannot be signed into, or null when they can.
 * Mirrors the API's refusals so the button explains itself instead of failing.
 */
export function whyNotImpersonable(
  u: { id: string; status: string; roles: Array<{ key: string }> },
  me: string | undefined,
): string | null {
  if (u.id === me) return 'This is your own account.';
  if (u.status !== 'ACTIVE') return 'The account is not active.';
  if (u.roles.some((r) => r.key === 'PLATFORM_ADMIN')) return 'Another Super Admin cannot be signed into.';
  return null;
}

/** Where the Super Admin was, so Stop brings them back to it. */
export const IMPERSONATION_RETURN_KEY = 'dru.impersonation.return';

/**
 * One click: signs the Super Admin in as somebody, with their full access,
 * and opens that person's console (owner's decision, 29 Oct 2026 — no
 * reason, no confirmation). The server still records who did it.
 *
 * `path` is the API route that starts it: a user's, or a physician's.
 */
export function useSignInAs() {
  const { impersonate } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function signInAs(path: string) {
    setBusy(path); setError(null);
    try {
      try { sessionStorage.setItem(IMPERSONATION_RETURN_KEY, `${location.pathname}${location.search}`); } catch { /* Stop goes to the overview instead */ }
      const res = await api.post<{ accessToken: string }>(path, { mode: 'full' });
      const principal = await impersonate(res.accessToken);
      nav(landingAfterImpersonation(principal, 'full'), { replace: true });
    } catch (e) { setError(errorMessage(e)); setBusy(null); }
  }
  return { signInAs, busy, error };
}

/** The row icon: sign in as this account. */
export function SignInAsAction({ name, path, signIn }: {
  name: string; path: string; signIn: ReturnType<typeof useSignInAs>;
}) {
  return (
    <IconAction
      label={`Sign in as ${name}`}
      icon={LogIn}
      disabled={signIn.busy !== null}
      onClick={() => void signIn.signInAs(path)}
    />
  );
}
